// askSmart-ui/src/pages/TOSP/budgetAllocation.ts
import {
  BudgetLine,
  CamCategory,
  CategorySplit,
  COMMERCIAL_SPACE_TYPE,
  emptySplitMapping,
  MANAGEMENT_FEE_ACCOUNT,
  MANAGEMENT_FEE_CATEGORY,
  MANAGEMENT_FEE_RATE,
  occupancyDayShare,
  PERCENT_SPLIT,
  SplitMapping,
  Tenant,
  tenantLabel,
  Unit,
} from "./types";
import { sameText } from "./validation";

/**
 * One unit's share of a category's budget, and who pays it.
 *
 * A unit that changed hands mid-period produces one entry PER OCCUPANT, so
 * `unitId` is not unique across the list — see `occupantsOf`.
 */
export interface UnitAllocation {
  unitId: string;
  suiteNumber: string;
  buildingId: string;
  tenantId: string | null;
  tenantName: string;
  sqFt: number;
  amount: number;
}

export interface AllocationResult {
  allocations: UnitAllocation[];
  /** Cost the split couldn't place — nothing occupied in scope, or no area. */
  unallocated: number;
  /** Why, when something couldn't be allocated or a share had to be moved. */
  note: string | null;
}

/**
 * Waste and common-area costs are charged against commercial space, so only
 * Commercial units take a share. Storage and back-of-house don't.
 */
const commercialUnits = (units: Unit[]): Unit[] =>
  units.filter((unit) => sameText(unit.spaceType, COMMERCIAL_SPACE_TYPE));

const zeroes = (): number[] => new Array(12).fill(0);

/**
 * The one row every unlet unit's share goes to. A single id, so a centre with
 * four empty units reads one "Unoccupied" line rather than four identical ones.
 */
const UNOCCUPIED_ID = "unoccupied";

const round2 = (value: number): number => Math.round(value * 100) / 100;

/** A tenancy's hold on one unit, month by month, as a fraction of each month. */
export interface UnitOccupant {
  tenantId: string;
  tenantName: string;
  months: number[];
}

/**
 * Who held a unit through a given year, and for how much of each month.
 *
 * CAM follows occupancy exactly as taxes and BMVA dues do — it switches to the
 * new tenant on the takeover date, pro-rated to the day. Read from the leases
 * rather than from "who holds it today", so a budget for a year already past
 * still divides between the tenancies that actually ran in it.
 *
 * Clamped against what earlier leases already hold, so two overlapping leases
 * on one unit can never claim more than a month is worth. Days NOBODY held are
 * reported as an "Unoccupied" remainder rather than being pushed onto the
 * other tenancies — see `attribute`. The same treatment the tax and BMVA
 * recoveries give it.
 */
/**
 * CAM FOLLOWS POSSESSION, NOT RENT.
 *
 * A tenant fitting out a unit rent-free still pays CAM: the lights are on, the
 * snow is cleared, the waste is collected. So a tenancy's hold on a unit runs
 * from the EARLIER of its lease commencement and its rent commencement —
 * whichever date first says the space was theirs.
 *
 * LCBO is the case that found this. Their lease commenced 2024-08-15 and rent
 * on 2025-02-12, so keying CAM to rent read January and half of February as
 * vacant — a two-month hole in a 5,380 sq ft unit that was never empty.
 *
 * Expressed as a substitute `space` so the day arithmetic stays in one place,
 * and deliberately NOT by changing `occupancyDayShare` itself: the tax and
 * BMVA recoveries share that, and they are not in scope here.
 *
 * Exported because the revenue budget asks the same question of the same
 * leases: who held this unit when the year opened. One definition, so the rent
 * line and the CAM line can never name different incumbents.
 */
export const possessionOf = (
  occupancy: { leaseCommenceDate?: string },
  space: { rentCommenceDate?: string },
): { rentCommenceDate: string } => {
  const rent = (space.rentCommenceDate || "").trim();
  const lease = (occupancy.leaseCommenceDate || "").trim();
  return { rentCommenceDate: rent && lease ? (rent < lease ? rent : lease) : rent || lease };
};

/**
 * The per-unit occupancy read, remembered.
 *
 * THIS IS THE HOT PATH OF THE WHOLE MODULE. Who holds a unit is asked for
 * every unit, of every category's every entity, in every month, for every
 * expense line — on this portfolio that is millions of reads of the same
 * ninety-odd answers, and it took the CAM recovery from a quarter of a second
 * to twenty-five.
 *
 * Keyed on the ARRAYS BY IDENTITY, not on their contents: the data context
 * hands the same arrays to every caller until something actually changes, and
 * a new array means new data, so the memo empties itself exactly when it
 * should. The arrays are never mutated in place anywhere in TOSP — a change
 * produces a new array — which is what makes this safe.
 */
let occupantsMemo: {
  tenants: Tenant[];
  year: number;
  byUnit: Map<string, UnitOccupant[]>;
} | null = null;

function occupantsCached(unit: Unit, tenants: Tenant[], year: number): UnitOccupant[] {
  if (!occupantsMemo || occupantsMemo.tenants !== tenants || occupantsMemo.year !== year) {
    occupantsMemo = { tenants, year, byUnit: new Map() };
  }
  const held = occupantsMemo.byUnit.get(unit.id);
  if (held) return held;
  const computed = occupantsOf(unit, tenants, year);
  occupantsMemo.byUnit.set(unit.id, computed);
  return computed;
}

export function occupantsOf(unit: Unit, tenants: Tenant[], year: number): UnitOccupant[] {
  const byTenant = new Map<string, UnitOccupant>();
  const covered = zeroes();

  for (const tenant of tenants) {
    for (const occupancy of tenant.occupancies || []) {
      for (const space of occupancy.leasedSpaces || []) {
        if (space.unitId !== unit.id) continue;
        for (let m = 0; m < 12; m += 1) {
          const share = Math.min(
            occupancyDayShare(occupancy, possessionOf(occupancy, space), year, m + 1),
            1 - covered[m],
          );
          if (share <= 0) continue;
          let row = byTenant.get(tenant.id);
          if (!row) {
            row = { tenantId: tenant.id, tenantName: tenantLabel(tenant), months: zeroes() };
            byTenant.set(tenant.id, row);
          }
          row.months[m] += share;
          covered[m] += share;
        }
      }
    }
  }

  return [...byTenant.values()];
}

/** The units one chosen entity brings into scope. */
/**
 * An entity's units, remembered for the same reason as the occupancy read: the
 * answer is identical for all twelve months and all hundred-odd expense lines,
 * and recomputing it per line per month is most of what the recovery was doing.
 */
let scopeMemo: {
  units: Unit[];
  tenants: Tenant[];
  year: number;
  assume: Record<string, string>;
  byKey: Map<string, Unit[]>;
} | null = null;

function unitsForEntity(
  splitAmong: SplitMapping["splitAmong"],
  entity: string,
  units: Unit[],
  tenants: Tenant[],
  year: number,
  /** unitId → tenantId: units whose occupancy the budget states. */
  assume: Record<string, string> = {},
): Unit[] {
  if (
    !scopeMemo ||
    scopeMemo.units !== units ||
    scopeMemo.tenants !== tenants ||
    scopeMemo.year !== year ||
    scopeMemo.assume !== assume
  ) {
    scopeMemo = { units, tenants, year, assume, byKey: new Map() };
  }
  const memoKey = `${splitAmong}\u0000${entity}`;
  const remembered = scopeMemo.byKey.get(memoKey);
  if (remembered) return remembered;
  const answer = resolveUnitsForEntity(splitAmong, entity, units, tenants, year, assume);
  scopeMemo.byKey.set(memoKey, answer);
  return answer;
}

function resolveUnitsForEntity(
  splitAmong: SplitMapping["splitAmong"],
  entity: string,
  units: Unit[],
  tenants: Tenant[],
  year: number,
  assume: Record<string, string>,
): Unit[] {
  const pool = commercialUnits(units);

  /**
   * THE TENANCY THE BUDGET CHARGES FOR A UNIT — stated on the budget, or
   * failing that whoever held it when the year opened.
   *
   * A category's scope is read through this and not through "anyone whose
   * lease touches the space during the year", because a budget cannot know
   * about a lease signed after it was drawn up. Columbia took I-2 over the
   * following October; reading their waste band put 1,580 sq ft into the
   * Retail Hi pool, thinned the rate from $1.8697 to $1.7559 and quietly took
   * $612 off LCBO's charge and $370 off Village Market's — on a budget
   * prepared eleven months before that lease existed.
   *
   * What actually happened in the year is the reconciliation's business. This
   * is the budget's, and the budget only knows who was there at the start.
   */
  const holderOf = (unit: Unit): Tenant | undefined => {
    const stated = assume[unit.id];
    if (stated) return tenants.find((candidate) => candidate.id === stated);
    const incumbent = incumbentOf(unit, tenants, year);
    return incumbent
      ? tenants.find((candidate) => candidate.id === incumbent.tenantId)
      : undefined;
  };

  switch (splitAmong) {
    case "Buildings":
      return pool.filter((unit) => unit.buildingId === entity);

    case "Units":
      return pool.filter((unit) => unit.id === entity);

    case "Tenants":
      return pool.filter((unit) => holderOf(unit)?.id === entity);

    case "Waste Removal":
      return pool.filter((unit) => {
        const band = holderOf(unit)?.wasteRemoval;
        return !!band && sameText(band, entity);
      });

    default:
      return [];
  }
}

const dedupe = (units: Unit[]): Unit[] =>
  units.filter((unit, i, all) => all.findIndex((u) => u.id === unit.id) === i);

/**
 * How much of `amount` each unit in scope carries.
 *
 * OCCUPANCY PLAYS NO PART HERE. A unit's share is settled by its area — or
 * evenly, or by the stated percentages — against every unit the split brings
 * in, let or empty.
 *
 * That is what makes a tenant's charge FIXED: it cannot move because a
 * neighbour commenced or terminated. Weighing by occupancy instead made the
 * division a function of who happened to be in the centre that month, so every
 * arrival quietly re-priced everybody else — twelve different figures for a
 * tenancy whose own circumstances never changed.
 */
function unitSharesOf(
  split: SplitMapping,
  amount: number,
  units: Unit[],
  tenants: Tenant[],
  year: number,
  /** unitId → tenantId: units whose occupancy the budget STATES. */
  assume: Record<string, string> = {},
): { shares: Map<string, number>; note: string | null } {
  const shares = new Map<string, number>();
  let note: string | null = null;

  // A UNIT WITH NO OCCUPANT WHEN THE YEAR OPENED IS NOT IN THE DIVISION.
  //
  // CAM is never the landlord's cost, so an unlet unit's share cannot sit
  // unrecovered — it has to be carried by the units that were let. Leaving the
  // unit out does exactly that: the remaining shares grow to fill the whole
  // cost, within whatever scope the category covers.
  //
  // BH4 is the case that settled it. The building was new, LCBO took #1, and #2
  // stood empty until Greekery arrived in August — so a budget drawn up the
  // previous November had one tenant in that building to charge, and charged it
  // for the building. That falls out of this rule with nothing to state.
  //
  // Not the same thing as a HANDOVER, where the unit did have an occupant and
  // the incumbent simply keeps it for the year. Both leave the incoming tenant
  // off the budget, for the same reason: it could not have known.
  // A STATED OCCUPANCY PUTS THE UNIT BACK IN. The rule is about what the
  // budget could have known, and an admin saying who was assumed to be paying
  // IS the budget knowing. BH4 182 #2 is the case: a new build, let from
  // August, and the old budget had the incoming tenant in it from January
  // because the lease was signed before the year began. Stating that on the
  // budget must put the unit back into the division — without this the
  // override could never fire, because the unit was dropped before anyone
  // looked at it.
  const occupied = (pool: Unit[]): Unit[] =>
    pool.filter((unit) => !!assume[unit.id] || !!incumbentOf(unit, tenants, year));

  const byMeasure = (all: Unit[], value: number, byArea: boolean): string | null => {
    const pool = occupied(all);
    if (pool.length === 0) {
      return all.length === 0
        ? "No units in scope."
        : "No unit in scope was let when the year opened, so there is nobody to charge.";
    }
    const total = byArea
      ? pool.reduce((sum, unit) => sum + (unit.occupancySqFt || 0), 0)
      : pool.length;
    if (total <= 0) return "The units in scope have no recorded area.";
    pool.forEach((unit) => {
      const measure = byArea ? unit.occupancySqFt || 0 : 1;
      // A unit reachable through two entities takes both shares.
      shares.set(unit.id, (shares.get(unit.id) || 0) + (value * measure) / total);
    });
    return null;
  };

  if (split.basedOn === PERCENT_SPLIT) {
    split.entities.forEach((entity) => {
      const percentage = split.percentages?.[entity] ?? 0;
      const pool = unitsForEntity(split.splitAmong, entity, units, tenants, year, assume);
      const problem = byMeasure(pool, (amount * percentage) / 100, true);
      if (problem && !note) note = problem;
    });
  } else {
    const pool = dedupe(
      split.entities.flatMap((entity) =>
        unitsForEntity(split.splitAmong, entity, units, tenants, year, assume),
      ),
    );
    note = byMeasure(pool, amount, split.basedOn === "Unit Sqft");
  }

  return { shares, note };
}

/**
 * A unit's share handed to whoever occupied it.
 *
 * `month` is 0–11 for one month, or null for the year. Days nobody held become
 * an "Unoccupied" row — exactly as the Real Estate Taxes and BMVA Basic
 * recoveries already do. A unit standing empty is a shortfall to show, not a
 * cost to move onto the tenants next door: doing that would change their fixed
 * monthly charge, which is the one thing this must never do.
 */
function attribute(
  unit: Unit,
  unitAmount: number,
  tenants: Tenant[],
  year: number,
  month: number | null,
  /** unitId → tenantId: this unit's CAM is charged wholly to that tenancy. */
  assume: Record<string, string> = {},
): UnitAllocation[] {
  const describe = (
    tenantId: string | null,
    tenantName: string,
    amount: number,
  ): UnitAllocation => ({
    unitId: unit.id,
    suiteNumber: unit.suiteNumber,
    buildingId: unit.buildingId,
    tenantId,
    tenantName,
    sqFt: unit.occupancySqFt || 0,
    amount,
  });

  // A BUDGET IS PREPARED BEFORE THE YEAR STARTS, AND IT DOES NOT KNOW.
  //
  // Whoever holds a unit when the budget is drawn up is assumed to pay for it
  // all year, in twelve even instalments. Nobody budgets for a tenant leaving
  // in June — in November they have no reason to think it will happen — so a
  // budget that split a unit between two tenancies would be describing a year
  // it could not have foreseen.
  //
  // So the whole year goes to ONE tenancy: the one stated on the budget, or
  // failing that the incumbent at the start of the year. A handover later in
  // the year moves nothing. What actually happened is a matter for the year-end
  // reconciliation, which credits the outgoing tenancy and charges the
  // incoming one — not for the budget, which must stop moving once it is done.
  //
  // This is deliberately NOT how the tax and BMVA recoveries behave. They
  // still follow occupancy to the day, which is right for them and is what was
  // asked for; CAM alone is settled this way.
  const stated = assume[unit.id];
  const holder = stated
    ? tenants.find((candidate) => candidate.id === stated)
    : incumbentOf(unit, tenants, year);

  if (holder) {
    const id = "tenantId" in holder ? holder.tenantId : holder.id;
    const label = "tenantName" in holder ? holder.tenantName : tenantLabel(holder);
    return [describe(id, label, unitAmount)];
  }

  // Nobody held it when the year opened and nothing has been stated, so there
  // is no tenancy the budget can charge. Shown rather than spread.
  return [describe(null, "Unoccupied", unitAmount)];
}

/**
 * The tenancy holding a unit at the START of the budget year.
 *
 * January's occupant, and the one with the largest share of it where a
 * handover fell inside that month. Null where nobody held it — a unit let
 * part-way through the year was not in the budget at all, and giving its new
 * tenant twelve months would bill them for a period nobody expected them to
 * pay for. That case needs somebody to say what was assumed.
 */
function incumbentOf(
  unit: Unit,
  tenants: Tenant[],
  year: number,
): UnitOccupant | undefined {
  return occupantsCached(unit, tenants, year)
    .filter((occupant) => occupant.months[0] > 0.005)
    .sort((a, b) => b.months[0] - a.months[0])[0];
}

/** Merges claims on the same unit by the same tenant, and rounds to cents. */
function settle(allocations: UnitAllocation[], amount: number): UnitAllocation[] {
  const merged: UnitAllocation[] = [];
  for (const allocation of allocations) {
    const existing = merged.find(
      (m) => m.unitId === allocation.unitId && m.tenantId === allocation.tenantId,
    );
    if (existing) existing.amount += allocation.amount;
    else merged.push({ ...allocation });
  }

  merged.forEach((allocation) => {
    allocation.amount = round2(allocation.amount);
  });

  // Rounding remainders land on the largest share so the parts still add up to
  // the whole.
  const drift = round2(amount - merged.reduce((sum, a) => sum + a.amount, 0));
  if (drift !== 0 && merged.length > 0) {
    const largest = merged.reduce((a, b) => (b.amount > a.amount ? b : a));
    largest.amount = round2(largest.amount + drift);
  }

  return merged;
}

/**
 * A category's budget divided into what each tenancy owes.
 *
 * Two steps, deliberately separate: the split settles what each UNIT carries,
 * then occupancy decides who pays it. "% Split" fixes each entity's share of
 * the cost first and divides that share across the entity's units by area; the
 * other bases work from a single pool of every unit the chosen entities bring
 * in.
 *
 * `month` is 0–11 to divide one month's cost, or null (the default) for a
 * year's. Either way a unit's share is the same — only who carries it changes.
 */
export function allocateCategory(
  split: SplitMapping,
  amount: number,
  units: Unit[],
  tenants: Tenant[],
  year: number,
  month: number | null = null,
  /** unitId → tenantId, for units whose occupancy the budget states. */
  assume: Record<string, string> = {},
): AllocationResult {
  if (split.ignored) {
    return { allocations: [], unallocated: 0, note: "Excluded from calculations." };
  }
  if (!split.splitAmong || (split.entities?.length ?? 0) === 0 || !split.basedOn) {
    return { allocations: [], unallocated: amount, note: "No split configured." };
  }

  const { shares, note } = unitSharesOf(split, amount, units, tenants, year, assume);
  if (shares.size === 0) {
    return { allocations: [], unallocated: round2(amount), note: note || "No units in scope." };
  }

  const byId = new Map(units.map((unit) => [unit.id, unit]));
  const allocations: UnitAllocation[] = [];
  shares.forEach((unitAmount, unitId) => {
    const unit = byId.get(unitId);
    if (unit) allocations.push(...attribute(unit, unitAmount, tenants, year, month, assume));
  });

  return { allocations: settle(allocations, amount), unallocated: 0, note };
}

/**
 * A line's twelve months. Lines imported before the QuickBooks layout was read
 * properly carry only a year's figure; spread it rather than reading as nothing,
 * or a budget built on one would divide a cost it can see into no shares at all.
 * The expense panel's banner is what tells the admin to re-import.
 */
const monthsOf = (line: BudgetLine): number[] =>
  line.months && line.months.length === 12
    ? line.months
    : new Array(12).fill((line.amount || 0) / 12);

/** A line's year. */
const annualOf = (line: BudgetLine): number =>
  monthsOf(line).reduce((a, b) => a + b, 0);

/**
 * The QuickBooks account the CAM recovery is keyed into. Named here so the fee
 * can keep it out of its own base once it comes back on an import — see
 * `managementFeeMonths`.
 */
const CAM_RECOVERY_CODE = "41001";

/** The fee line TOSP inserts itself, which is never part of its own base. */
export const isManagementFeeLine = (line: BudgetLine): boolean =>
  !!line.locked || sameText(line.description, MANAGEMENT_FEE_ACCOUNT);

const splitFor = (
  line: BudgetLine,
  splits: CategorySplit[],
): CategorySplit | undefined =>
  line.camCategory
    ? splits.find((split) => sameText(split.camCategory, line.camCategory))
    : undefined;

/**
 * A line carrying a category that is marked Excluded.
 *
 * Distinct from a line with no category at all, which is an omission rather
 * than a decision — an uncategorised line still needs attention and stays on
 * screen.
 */
export function isExcludedLine(line: BudgetLine, splits: CategorySplit[]): boolean {
  return !!splitFor(line, splits)?.ignored;
}

/**
 * The expense lines whose cost is recovered from tenants: real accounts with a
 * category that isn't Excluded. Headers carry no money, income is TOSP's own
 * Revenue Budget, and a line with no category can't be divided at all.
 */
export function recoverableLines(
  lines: BudgetLine[],
  splits: CategorySplit[],
): BudgetLine[] {
  return lines.filter((line) => {
    if (line.isHeader || line.section === "income" || !line.camCategory) return false;
    const split = splitFor(line, splits);
    return !!split && !split.ignored;
  });
}

/** Account names compared the way a person would: case and spacing aside. */
const plainly = (text: string | undefined): string =>
  (text || "").trim().replace(/\s+/g, " ").toLowerCase();

/**
 * The token each expense line's edits are stored against, for a whole budget.
 *
 * KEYED BY ACCOUNT CODE, not by the line's id. An import replaces every line
 * and its id with it, so an id-keyed edit would be orphaned by the next
 * re-import — invisible, unclearable, and still on the document.
 *
 * But a code is only the right key if it identifies ONE line, and it doesn't
 * always. Two things are guarded here:
 *
 *  - DUPLICATE CODES. The live 2025 budget already carries two lines coded
 *    55001, and a bare-code key would make one edit change both. Repeats take
 *    an ordinal, so the first stays a bare code — existing edits keep working
 *    — and the second becomes `55001#2`.
 *  - LINES WITH NO CODE. Twelve of the ninety-five expense leaves have none:
 *    Wages, Purchases, Health Insurance, Cost of Goods Sold and others. Those
 *    key on the account NAME, which does survive a re-import, where the id
 *    emphatically does not.
 *
 * Derived for the whole set at once because neither guard can be decided from
 * one line in isolation.
 */
export function expenseLineTokens(lines: BudgetLine[]): Map<string, string> {
  const seen = new Map<string, number>();
  const tokens = new Map<string, string>();

  for (const line of lines) {
    if (line.isHeader) continue;
    const code = (line.code || "").trim();
    // `~` marks a name-keyed line, so it can never collide with a code.
    const base = code || `~${plainly(line.description)}`;
    const nth = (seen.get(base) || 0) + 1;
    seen.set(base, nth);
    tokens.set(line.id, nth === 1 ? base : `${base}#${nth}`);
  }

  return tokens;
}

/** The key one month of one line's edits is stored under. */
export const expenseLineKey = (token: string, month: number): string =>
  `${token}:${month}`;

/**
 * Whether an edit may be applied to a line at all.
 *
 * The management fee is computed from the other lines, so an edit against it
 * would be arguing with arithmetic. Its cells aren't offered, but a key left
 * behind by an earlier scheme — or typed into the document by hand — must not
 * reach it either.
 */
const acceptsEdits = (line: BudgetLine): boolean =>
  !line.isHeader && !isManagementFeeLine(line);

/**
 * The imported lines with the admin's edits applied.
 *
 * A budget line is a FORECAST, not a posting. The figure arrives from
 * QuickBooks and is usually right, but "Seasons will cost 22,000 in June" is a
 * judgement that gets revised, and revising it in QuickBooks and re-importing
 * to change one number is friction for nothing — the final figures are keyed
 * back there at the end regardless.
 *
 * The imported figure is kept underneath rather than overwritten, so the cell
 * can read as an edit and clearing it restores what QuickBooks said.
 *
 * Everything downstream reads lines through this: the grid, the management fee
 * and the 41001 recovery. An edit therefore moves the tenant allocation, the
 * fee and the recovery together, which is the whole point of making it.
 */
export function effectiveLines(
  lines: BudgetLine[],
  overrides: Record<string, number> = {},
): BudgetLine[] {
  if (Object.keys(overrides).length === 0) return lines;
  const tokens = expenseLineTokens(lines);

  return lines.map((line) => {
    if (!acceptsEdits(line)) return line;
    const token = tokens.get(line.id);
    if (!token) return line;

    const base = monthsOf(line);
    let touched = false;
    const months = base.map((amount, month) => {
      const own = overrides[expenseLineKey(token, month)];
      if (own === undefined) return amount;
      touched = true;
      return own;
    });
    if (!touched) return line;
    return { ...line, months, amount: round2(months.reduce((a, b) => a + b, 0)) };
  });
}

/**
 * The edits that still belong to a budget after an import replaced its lines.
 *
 * Two kinds go, and both would otherwise sit on the document unreachable:
 *
 *  - an edit whose account is no longer in the export at all;
 *  - an edit whose code is still there but now carries a DIFFERENT NAME.
 *
 * The second is the one worth the trouble. An admin edited "51001 Seasons"; if
 * 51001 comes back as something else, applying their figure to it would put a
 * number they never typed against an account they never looked at. Dropping it
 * reverts that month to what QuickBooks sent, which is the safe direction —
 * and the amber edit marker disappearing is visible, where a misapplied figure
 * would not be.
 *
 * The cost is that renaming an account discards edits against it. That is the
 * trade I have taken deliberately: a rename is rare, the edits are quick to
 * retype, and the alternative failure is silent and wrong.
 */
export function prunedExpenseEdits(
  before: BudgetLine[],
  after: BudgetLine[],
  overrides: Record<string, number> = {},
): Record<string, number> {
  const keys = Object.keys(overrides);
  if (keys.length === 0) return {};

  const nameFor = (lines: BudgetLine[]): Map<string, string> => {
    const tokens = expenseLineTokens(lines);
    const names = new Map<string, string>();
    lines.forEach((line) => {
      const token = tokens.get(line.id);
      if (token && acceptsEdits(line)) names.set(token, plainly(line.description));
    });
    return names;
  };

  const was = nameFor(before);
  const now = nameFor(after);

  const survives = keys.filter((key) => {
    const token = key.slice(0, key.lastIndexOf(":"));
    const name = now.get(token);
    if (name === undefined) return false;
    // Unknown beforehand — an edit older than this scheme — is kept rather
    // than thrown away on a comparison that can't be made.
    const earlier = was.get(token);
    return earlier === undefined || earlier === name;
  });

  return Object.fromEntries(survives.map((key) => [key, overrides[key]]));
}

/**
 * What an allocation's rows are gathered under.
 *
 * "tenancy" is the budget's own reading: who pays. "unit" is the same
 * arithmetic gathered by SPACE instead, which is what a handover needs — when
 * a unit changes hands part-way through the year the two tenancies divide that
 * unit's figures between them, and for that the unit's own figures have to
 * exist on their own rather than folded into whoever the budget charged.
 *
 * The division itself is identical either way: both group the very same
 * per-unit allocations, so a month's total cannot differ between them.
 */
export type AllocationGrouping = "tenancy" | "unit";

const groupOf = (
  allocation: UnitAllocation,
  by: AllocationGrouping,
): { id: string; label: string } =>
  by === "unit"
    ? { id: allocation.unitId, label: allocation.suiteNumber }
    : { id: allocation.tenantId || UNOCCUPIED_ID, label: allocation.tenantName };

/** One tenancy's — or one unit's — share of a single expense line, month by month. */
export interface LineAllocationRow {
  id: string;
  label: string;
  months: number[];
  total: number;
}

export interface LineAllocation {
  rows: LineAllocationRow[];
  /** Cost no occupant could be found for, across the year. */
  unallocated: number;
  note: string | null;
}

/**
 * One expense line divided across the tenancies, month by month.
 *
 * NOTHING IS ADJUSTABLE HERE. Who carries a cost is settled by the category's
 * split, configured once under Configuration → CAM Categories, and read the
 * same way for every line that category covers. An edit against one tenancy's
 * share would be a local exception to a rule kept in one place, and the next
 * change to the split would silently overrule it.
 *
 * The figure to change is the LINE's — see `effectiveLines`. Changing the cost
 * moves every tenancy's share of it, in the proportions the split states.
 */
export function allocateLineMonths(
  line: BudgetLine,
  split: SplitMapping | undefined,
  units: Unit[],
  tenants: Tenant[],
  year: number,
  /** unitId → tenantId, for units whose occupancy the budget states. */
  assume: Record<string, string> = {},
  /** Per tenancy by default; per unit for the handover split. */
  groupBy: AllocationGrouping = "tenancy",
): LineAllocation {
  const cost = monthsOf(line);
  const rows = new Map<string, LineAllocationRow>();
  let unallocated = 0;
  let note: string | null = null;

  for (let month = 0; month < 12; month += 1) {
    const divided = split
      ? allocateCategory(split, cost[month], units, tenants, year, month, assume)
      : { allocations: [], unallocated: cost[month], note: null };
    if (divided.note && !note) note = divided.note;
    unallocated += divided.unallocated;

    // A tenant's several units are merged: a shop, a patio and a locker are
    // one bill. Grouped by unit instead, they stay apart — which is the point
    // of that grouping.
    for (const allocation of divided.allocations) {
      const { id, label } = groupOf(allocation, groupBy);
      let row = rows.get(id);
      if (!row) {
        row = { id, label, months: zeroes(), total: 0 };
        rows.set(id, row);
      }
      row.months[month] += allocation.amount;
    }
  }

  const ordered = [...rows.values()].sort((a, b) => a.label.localeCompare(b.label));
  ordered.forEach((row) => {
    row.months = row.months.map(round2);
    row.total = round2(row.months.reduce((a, b) => a + b, 0));
  });

  return { rows: ordered, unallocated: round2(unallocated), note };
}

/**
 * The management fee's twelve monthly figures: 4% of everything the centre
 * collects.
 *
 * What makes this more than a multiplication is that the fee is recovered
 * inside CAM, and CAM is one of the things collected — so the fee is part of
 * its own base. Written out, with R for the revenue other than CAM and C for
 * the CAM expenses:
 *
 *     41001 recovered = C + fee            (the recovery is the costs + the fee)
 *     fee             = 4% × (R + 41001)   (4% of everything collected)
 *
 * Substituting gives fee = 4% × (R + C + fee), so
 *
 *     fee = (R + C) × rate / (1 - rate)
 *
 * which at 4% is a twenty-fourth of R + C. Solved once rather than iterated:
 * computing a fee, adding it to CAM and taking 4% again would climb on every
 * pass and never settle. That is the double dip to avoid — not the fee's
 * presence in its own base, which is simply what charging on gross collections
 * means.
 *
 * VERIFIED against the centre's 2025 budget: C was $918,215.08 and the fee
 * $388,158.06, and the 41001 keyed into QuickBooks was $1,306,373.76 — the two
 * added together, to within 62 cents of rounding. The remaining 0.2% between
 * TOSP's figure and that one is the revenue this budget projects being some
 * $20,000 above what the old sheet carried, not a difference of method.
 *
 * The 41001 income line is kept OUT of the base deliberately: CAM is in it
 * already, as the expenses the recovery is built from. Counting the recovery as
 * well would charge the fee on CAM twice over.
 */
export function managementFeeMonths(
  lines: BudgetLine[],
  splits: CategorySplit[],
): number[] {
  const rate = MANAGEMENT_FEE_RATE / 100;
  /** Grossed up, because the fee is recovered and so collected. */
  const effective = rate / (1 - rate);
  // Income is imported as leaves only — never a header — so nothing here is
  // counted twice over a parent account.
  //
  // 41001 is the exception, and it has to be excluded by name: it arrives as an
  // income line the moment the recovery is keyed back into QuickBooks, and CAM
  // is in the base already as the expenses behind it.
  const income = lines.filter(
    (line) =>
      line.section === "income" &&
      !sameText(line.code || "", CAM_RECOVERY_CODE) &&
      !(line.description || "").trim().startsWith(CAM_RECOVERY_CODE),
  );
  const cam = recoverableLines(lines, splits).filter((line) => !isManagementFeeLine(line));
  const base = [...income, ...cam];
  return zeroes().map(
    (_, month) => base.reduce((sum, line) => sum + monthsOf(line)[month], 0) * effective,
  );
}

/**
 * The id the computed fee line always carries.
 *
 * Fixed rather than generated, because the line is now worked out on every
 * render instead of being inserted once. A fresh id each time would orphan any
 * allocation adjustment made against it, and would make the line a different
 * row to React on every pass.
 */
export const MANAGEMENT_FEE_LINE_ID = "bln-mgmt-fee";

/**
 * The management fee as a line, computed from the budget's own imported lines.
 *
 * NOT STORED. It used to be inserted into `budget.lines` by a button, which
 * made it a figure that could disagree with the expenses underneath it — and
 * did, every time a category or a split was edited afterwards. Deriving it
 * removes the staleness and the duplicate-row problem in one go: there is no
 * insertion to collide with the nil 55001 the QuickBooks export carries.
 *
 * Returns null where there is nothing to charge on, so a budget with no
 * expenses yet doesn't show a fee of zero as though one had been worked out.
 */
export function managementFeeLine(
  lines: BudgetLine[],
  splits: CategorySplit[],
): BudgetLine | null {
  const months = managementFeeMonths(lines, splits);
  const amount = months.reduce((a, b) => a + b, 0);
  if (amount <= 0) return null;
  return {
    id: MANAGEMENT_FEE_LINE_ID,
    description: MANAGEMENT_FEE_ACCOUNT,
    code: "55001",
    amount,
    months,
    section: "expense",
    // Locked: its category is fixed, because the fee is charged on the whole
    // property and so always divides the same way.
    locked: true,
    camCategory: MANAGEMENT_FEE_CATEGORY,
  };
}

/**
 * The expense lines with the computed fee in place.
 *
 * Any 55001 already present is REPLACED — the imported nil one from the export,
 * or a stored one left on a budget from before the fee was derived — and the
 * computed line takes its position, so it stays where QuickBooks put it.
 * Appended under its account number when there is none to replace.
 *
 * Everything that needs to see the fee goes through this: the expense grid, the
 * CAM pool, and the recovery. One place, so they cannot disagree about what the
 * fee is or whether it is recoverable.
 */
export function withManagementFee(
  lines: BudgetLine[],
  splits: CategorySplit[],
): BudgetLine[] {
  const fee = managementFeeLine(lines, splits);
  const at = lines.findIndex(isManagementFeeLine);
  const rest = lines.filter((line) => !isManagementFeeLine(line));
  if (!fee) return rest;

  if (at >= 0) {
    const before = lines.slice(0, at).filter((line) => !isManagementFeeLine(line)).length;
    return [...rest.slice(0, before), fee, ...rest.slice(before)];
  }

  // Under the 5500 Property Mgmt header where QuickBooks would put it: 5500
  // sorts before 55001, and 55002 Mgmt Salary after, so this falls between.
  const slot = rest.findIndex((line) => Number(line.code) > Number(fee.code));
  return slot < 0 ? [...rest, fee] : [...rest.slice(0, slot), fee, ...rest.slice(slot)];
}

/**
 * What each building's units are charged in CAM for a budget's year.
 *
 * A COST, not a recovery, which is why occupancy plays no part: a building's
 * CAM is what the categories charge against its units, whether they are let or
 * standing empty. `unitSharesOf` settles that directly, so this doesn't need
 * to know who occupied anything.
 *
 * The management fee is in it, since it is a recoverable CAM expense like any
 * other and leaving it out would understate every building.
 */
export function camByBuilding(
  lines: BudgetLine[],
  splits: CategorySplit[],
  units: Unit[],
  tenants: Tenant[],
  year: number,
  /** unitId → tenantId: units whose occupancy the budget states. */
  assume: Record<string, string> = {},
): Map<string, number> {
  const byBuilding = new Map<string, number>();
  const buildingOf = new Map(units.map((unit) => [unit.id, unit.buildingId]));
  const pool = recoverableLines(withManagementFee(lines, splits), splits);

  const byCategory = new Map<string, number>();
  for (const line of pool) {
    byCategory.set(
      line.camCategory,
      (byCategory.get(line.camCategory) || 0) + annualOf(line),
    );
  }

  for (const [camCategory, annual] of byCategory) {
    const split = splits.find((candidate) => sameText(candidate.camCategory, camCategory));
    if (!split) continue;
    const { shares } = unitSharesOf(split, annual, units, tenants, year, assume);
    shares.forEach((amount, unitId) => {
      const building = buildingOf.get(unitId);
      if (!building) return;
      byBuilding.set(building, (byBuilding.get(building) || 0) + amount);
    });
  }

  byBuilding.forEach((amount, building) => byBuilding.set(building, round2(amount)));
  return byBuilding;
}
/** A unit whose CAM the budget had to decide about, and what it decided. */
export interface CamOccupancyDecision {
  unitId: string;
  suiteNumber: string;
  buildingId: string;
  sqFt: number;
  /** That unit's whole-year CAM, so the decision carries a figure. */
  annual: number;
  /** The tenancy charged for the year, or null where there is nobody to charge. */
  charged: { id: string; label: string } | null;
  /** True where somebody stated it rather than it coming from the incumbent. */
  stated: boolean;
  /** Everyone who held it during the year, with the months they held. */
  candidates: { id: string; label: string; months: number[] }[];
}

/**
 * Units whose CAM needed a decision, and what the budget decided.
 *
 * Two kinds, and both belong in front of an admin:
 *
 *  - A HANDOVER. The leases say two tenancies held the unit, the budget charges
 *    one of them for the whole year, and somebody should see which. This is the
 *    case that used to disappear from view: once a predecessor was entered and
 *    the year was fully covered there was no "gap" left to report, while the
 *    budget was still quietly splitting a unit it should not have split.
 *  - NOBODY AT THE START OF THE YEAR. A unit let part-way through was not in
 *    the budget at all, so there is no incumbent to fall back on and the
 *    assumption has to be stated.
 *
 * A unit held all year by one tenancy is not a decision and is left out.
 */
export function camOccupancyDecisions(
  lines: BudgetLine[],
  splits: CategorySplit[],
  units: Unit[],
  tenants: Tenant[],
  year: number,
  assume: Record<string, string> = {},
): CamOccupancyDecision[] {
  // Each unit's whole-year CAM, across every category that reaches it.
  const annualByUnit = new Map<string, number>();
  const pool = recoverableLines(withManagementFee(lines, splits), splits);
  const byCategory = new Map<string, number>();
  pool.forEach((line) =>
    byCategory.set(line.camCategory, (byCategory.get(line.camCategory) || 0) + annualOf(line)),
  );
  byCategory.forEach((annual, camCategory) => {
    const split = splits.find((candidate) => sameText(candidate.camCategory, camCategory));
    if (!split) return;
    unitSharesOf(split, annual, units, tenants, year, assume).shares.forEach((amount, unitId) =>
      annualByUnit.set(unitId, (annualByUnit.get(unitId) || 0) + amount),
    );
  });

  // UNITS THAT FELL OUT OF THE DIVISION STILL NEED TO BE OFFERED. A unit
  // nobody held when the year opened carries nothing, so it is absent from the
  // shares above — and an admin who wants to state its occupancy would have
  // nowhere to do it. Every unit any category's scope can reach is considered,
  // at nil, so the decision can be taken and the unit brought back in.
  splits.forEach((split) => {
    (split.entities || []).forEach((entity) => {
      unitsForEntity(split.splitAmong, entity, units, tenants, year, assume).forEach((unit) => {
        if (!annualByUnit.has(unit.id)) annualByUnit.set(unit.id, 0);
      });
    });
  });

  const decisions: CamOccupancyDecision[] = [];

  annualByUnit.forEach((annual, unitId) => {
    const unit = units.find((candidate) => candidate.id === unitId);
    if (!unit) return;

    const occupants = occupantsOf(unit, tenants, year);
    const incumbent = occupants
      .filter((occupant) => occupant.months[0] > 0.005)
      .sort((a, b) => b.months[0] - a.months[0])[0];

    // A unit one tenancy held all year is not a decision: the budget charges
    // them and there is nothing to say. What is left is a handover, or a unit
    // nobody held when the year opened — which carries nothing until somebody
    // states who was assumed to be paying for it.
    if (occupants.length === 0) return;
    if (occupants.length < 2 && incumbent) return;

    const statedId = assume[unitId];
    const statedTenant = statedId
      ? tenants.find((candidate) => candidate.id === statedId)
      : undefined;

    decisions.push({
      unitId,
      suiteNumber: unit.suiteNumber,
      buildingId: unit.buildingId,
      sqFt: unit.occupancySqFt || 0,
      annual: round2(annual),
      charged: statedTenant
        ? { id: statedTenant.id, label: tenantLabel(statedTenant) }
        : incumbent
          ? { id: incumbent.tenantId, label: incumbent.tenantName }
          : null,
      stated: !!statedTenant,
      candidates: occupants.map((occupant) => ({
        id: occupant.tenantId,
        label: occupant.tenantName,
        months: occupant.months,
      })),
    });
  });

  return decisions.sort((a, b) => b.annual - a.annual);
}

/** One tenancy's CAM recovery across the year. */
export interface CamRecoveryRow {
  id: string;
  label: string;
  months: number[];
  total: number;
}

export interface CamRecoveryResult {
  /** What each month recovers — the sum of the rows, so the two can't differ. */
  months: number[];
  total: number;
  rows: CamRecoveryRow[];
  /** Cost that no occupied space could be found for. */
  unallocated: number;
  /** One line per category that couldn't be divided cleanly. */
  notes: string[];
}

/**
 * What the CAM expenses recover, tenancy by tenancy.
 *
 * A TENANCY'S ANNUAL COMMITMENT DIVIDED BY TWELVE IS ITS FIXED MONTHLY CHARGE,
 * and that figure is identical in every month it is in occupancy — whether it
 * occupies for twelve months, eleven or fewer. Nothing another tenancy does
 * can move it: commencements and terminations elsewhere do not re-price
 * anybody, because the division is settled against the whole scope rather than
 * against whoever happens to be in the centre that month.
 *
 * The consequence, and it is the right one: space nobody held is not billed.
 * It appears as an "Unoccupied" row, the same way the Real Estate Taxes and
 * BMVA Basic recoveries already show it, so the line still totals the budget
 * and the shortfall is visible rather than loaded onto the neighbours.
 *
 * A tenant's figure is the sum of their share of every category they fall
 * under — Garbage F&B Hi, their building, All Cam — not one category's.
 *
 * The lumpiness of the costs themselves (snow in January, nothing in July)
 * belongs to the expense side and is read there; what is BILLED is twelve
 * equal instalments, because that is how it is invoiced.
 */
export function calculateCamRecovery(
  lines: BudgetLine[],
  splits: CategorySplit[],
  units: Unit[],
  tenants: Tenant[],
  year: number,
  /**
   * unitId → tenantId: units whose occupancy the budget states rather than
   * derives. See `attribute`.
   */
  assume: Record<string, string> = {},
  /** Per tenancy by default; per unit for the handover split. */
  groupBy: AllocationGrouping = "tenancy",
): CamRecoveryResult {
  const byCategory = new Map<string, BudgetLine[]>();
  // The fee is a recoverable expense like any other, so it enters the pool
  // here rather than being added on afterwards. It is computed, not stored —
  // see withManagementFee — which is why the pool is built from that rather
  // than from `lines`.
  for (const line of recoverableLines(withManagementFee(lines, splits), splits)) {
    const held = byCategory.get(line.camCategory);
    if (held) held.push(line);
    else byCategory.set(line.camCategory, [line]);
  }

  const rows = new Map<string, CamRecoveryRow>();
  const notes: string[] = [];
  /** What each month should bill: the sum of every category's flat instalment. */
  const target = zeroes();
  let unallocated = 0;

  const row = (id: string, label: string): CamRecoveryRow => {
    let found = rows.get(id);
    if (!found) {
      found = { id, label, months: zeroes(), total: 0 };
      rows.set(id, found);
    }
    return found;
  };

  for (const [camCategory, categoryLines] of byCategory) {
    const split = splits.find((candidate) => sameText(candidate.camCategory, camCategory));
    if (!split) continue;

    const annual = categoryLines.reduce((sum, line) => sum + annualOf(line), 0);
    // The flat instalment: a twelfth of the year, because that is how CAM is
    // billed, whatever shape the costs behind it take.
    const monthly = round2(annual / 12);

    // Each line's own division, from the category's split. An edit to a
    // line's COST arrives here already applied — the caller passes effective
    // lines — so changing 51001 Seasons from 22,000 to 25,000 moves every
    // tenancy's share of it, and the recovery with them.
    const divisions = categoryLines.map((line) =>
      allocateLineMonths(line, split, units, tenants, year, assume, groupBy),
    );
    divisions.forEach((division) => {
      if (division.note) notes.push(`${camCategory}: ${division.note}`);
    });

    for (let month = 0; month < 12; month += 1) {
      // WHO CARRIES THIS MONTH'S COST, AND IN WHAT PROPORTION.
      //
      // The costs settle the shares; the instalment is divided by those
      // shares. Safe to do per month now that a unit's share no longer depends
      // on who else is in occupation — every line in a category divides the
      // same way, so a tenancy's proportion is identical month to month and
      // its charge stays fixed. It was the occupancy-weighted denominator, not
      // this, that used to make a full-year tenancy's figure wander.
      const carried = new Map<string, { label: string; amount: number }>();
      divisions.forEach((division) => {
        division.rows.forEach((entry) => {
          if (entry.months[month] <= 0) return;
          const found = carried.get(entry.id);
          if (found) found.amount += entry.months[month];
          else carried.set(entry.id, { label: entry.label, amount: entry.months[month] });
        });
      });

      const total = [...carried.values()].reduce((sum, value) => sum + value.amount, 0);

      if (total > 0.005) {
        carried.forEach((value, id) => {
          row(id, value.label).months[month] += round2((monthly * value.amount) / total);
        });
        // Billed in full, so the whole instalment is this month's to account
        // for. Tracked rather than assumed, because the branch below may place
        // less than it was given.
        target[month] += monthly;
        continue;
      }

      // Nothing was spent this month, so the costs can't say who carries it —
      // but the instalment is still billed. Falls back to the plain split,
      // which gives the same proportions a costed month would.
      const plain = allocateCategory(split, monthly, units, tenants, year, month, assume);
      unallocated += plain.unallocated;
      // Only what could be placed. Counting the whole instalment here would
      // make the rounding correction below hand unallocatable cost to whoever
      // happened to be the largest row — inventing a charge out of a shortfall.
      target[month] += round2(monthly - plain.unallocated);
      if (plain.note) notes.push(`${camCategory}: ${plain.note}`);
      plain.allocations.forEach((allocation) => {
        const { id, label } = groupOf(allocation, groupBy);
        row(id, label).months[month] += allocation.amount;
      });
    }
  }

  // Alphabetical, as the revenue contributions are — a recovery is read tenant
  // by tenant, not largest first.
  // Alphabetical, as the revenue contributions are — a recovery is read tenant
  // by tenant, not largest first.
  const ordered = [...rows.values()].sort((a, b) => a.label.localeCompare(b.label));
  ordered.forEach((row) => {
    row.months = row.months.map(round2);
  });

  // THE LINE BILLS THE BUDGET EXACTLY, every month.
  //
  // Rounding fifty-odd rows to the cent leaves a few cents either way, which
  // made the twelve monthly totals differ by up to a dime — trivial money and
  // an obvious question, since the whole point of the line is that it reads the
  // same every month. The remainder goes to the largest row, the same way a
  // category's own division settles its rounding.
  // ONE ROW CARRIES THE ROUNDING, all year.
  //
  // Picking the largest row each month separately balanced the line but put a
  // couple of cents into a different tenancy every month, so two dozen fixed
  // charges stopped reading as fixed. Chosen once, and from the rows charged
  // in every month — a row that drops to nil in December can't absorb
  // December's cent without inventing a charge there.
  const everyMonth = ordered.filter((row) => row.months.every((amount) => amount > 0.005));
  const sink = (everyMonth.length > 0 ? everyMonth : ordered).reduce<
    CamRecoveryRow | undefined
  >((a, b) => (!a || b.months.reduce((x, y) => x + y, 0) > a.months.reduce((x, y) => x + y, 0) ? b : a), undefined);

  const months = zeroes().map((_, month) => {
    const placed = round2(ordered.reduce((sum, row) => sum + row.months[month], 0));
    const drift = round2(target[month] - placed);
    // Guarded: a drift the size of a real shortfall is not rounding, and must
    // not be handed to a tenancy as a charge.
    if (!sink || drift === 0 || Math.abs(drift) > 0.5) return placed;
    sink.months[month] = round2(sink.months[month] + drift);
    return target[month];
  });

  ordered.forEach((row) => {
    row.total = round2(row.months.reduce((a, b) => a + b, 0));
  });

  return {
    months,
    total: round2(months.reduce((a, b) => a + b, 0)),
    rows: ordered,
    unallocated: round2(unallocated),
    notes,
  };
}

// The splits a budget should carry, given how its lines are categorised.
//
// A category's split is SNAPSHOTTED onto the budget the first time the budget
// uses it, and never re-read from Configuration afterwards — editing a
// category later must not silently re-divide a budget already built. Splits
// for categories no longer used by any line are dropped, so a reassignment
// doesn't leave an orphan behind.
//
// Shared by the setup wizard and by inline editing on the budget screen, so
// the two can't disagree about what a budget's splits are.
export function resolveCategorySplits(
  lines: BudgetLine[],
  existing: CategorySplit[],
  camCategories: CamCategory[],
): CategorySplit[] {
  const used = lines.reduce<string[]>((acc, line) => {
    // Income is never categorised — see BudgetLine.section.
    if (line.section === "income") return acc;
    if (line.camCategory && !acc.includes(line.camCategory)) acc.push(line.camCategory);
    return acc;
  }, []);

  // The management fee's category, always, once there is any cost to charge on.
  // The fee is computed rather than imported, so no line in `lines` carries its
  // category — and without this its split would never be snapshotted onto the
  // budget, leaving the one line TOSP produces itself with nothing to divide by.
  const hasCosts = lines.some((line) => line.section !== "income" && !line.isHeader);
  if (hasCosts && !used.includes(MANAGEMENT_FEE_CATEGORY)) used.push(MANAGEMENT_FEE_CATEGORY);

  return used.map((camCategory) => {
    const already = existing.find((split) => split.camCategory === camCategory);
    if (already) return already;
    const configured = camCategories.find((category) => category.name === camCategory);
    return { camCategory, ...(configured?.split || emptySplitMapping()) };
  });
}
